import { toChannelEnv } from './map-connection-to-row';
import type { PlatformConnection } from './get-pype-connections';
import { getPlatformLogo } from './platform-logo';
import { isAgentProPlatform } from './platform-catalog';
import {
  excludeAgentProConnections,
  isAgentProConnection,
  type UnconnectedStatus,
} from './unconnected-channels';

// How a channel's Live Agent Platform binding is read through the project's
// connections (PSTUDIO-4344). Channel Settings uses it, and the Live Agent
// Platforms page lists the same options in its "Channels not connected" picker,
// so the two cannot disagree about which connections exist.

/** A project-level Live Agent Platform connector, as a dropdown option. */
export interface PlatformConnectionOption {
  id: string;
  name: string;
  /** The connector's `agent_routing`. Informational: the backend writes it. */
  agentRouting: string;
  /** Pre-resolved platform logo. */
  logo: string;
}

const boundTo = (
  pypes: { pypeId: string; env: string }[],
  pypeId: string,
  env: string
) =>
  pypes.some(
    (pype) =>
      pype.pypeId === pypeId && pype.env.toLowerCase() === env.toLowerCase()
  );

/**
 * Every SUPPORTED connection this pype+env is bound to. The UI is
 * single-select, but the schema permits a pype in several connections at once
 * (the unique index is per connection+pype+env), so callers see them all.
 *
 * AgentPro is not a supported platform: a channel bound only to it reads as not
 * connected, exactly like a channel never connected. Its bindings are read by
 * `findBoundAgentProConnectionIds`, because saving a real connection has to
 * detach them.
 */
export const findBoundConnectionIds = (
  connections: PlatformConnection[] | null | undefined,
  pypeId: string | undefined,
  env: string | undefined
): string[] => {
  if (!pypeId || !env) return [];
  return excludeAgentProConnections(connections)
    .filter((connection) => boundTo(connection.pypes, pypeId, env))
    .map((connection) => connection.id);
};

/** The AgentPro connections this pype+env is still bound to, to detach on save. */
export const findBoundAgentProConnectionIds = (
  connections: PlatformConnection[] | null | undefined,
  pypeId: string | undefined,
  env: string | undefined
): string[] => {
  if (!pypeId || !env) return [];
  return (connections ?? [])
    .filter(
      (connection) =>
        isAgentProConnection(connection) &&
        boundTo(connection.pypes, pypeId, env)
    )
    .map((connection) => connection.id);
};

/** The project's supported connections for one env, as dropdown options. */
export const toPlatformConnectionOptions = (
  connections: PlatformConnection[] | null | undefined,
  env: string | undefined
): PlatformConnectionOption[] =>
  excludeAgentProConnections(connections)
    .filter((connection) => toChannelEnv(connection.environment) === env)
    .map((connection) => ({
      id: connection.id,
      name: connection.name,
      agentRouting: connection.agentRouting,
      logo: getPlatformLogo(connection.platformId),
    }));

export interface ConnectionBinding {
  /** The supported connection the dropdown shows ('' = none). */
  selectedId: string;
  /**
   * Every connection a save detaches: the supported binding shown in the
   * dropdown, plus ALL the AgentPro bindings. The invoke detaches one connection
   * per call, so `planAssignmentSteps` turns this list into one call each.
   */
  fromConnectionIds: string[];
  /** Why the channel reads as not connected, or `null` when it is connected. */
  unconnectedStatus: UnconnectedStatus | null;
}

/**
 * One reading of a channel's Live Agent Platform binding. `agentRouting` is the
 * pype's own routing where the caller has it: a pype that still says AgentPro
 * with no connection row is "was connected" too.
 */
export const resolveConnectionBinding = ({
  connections,
  pypeId,
  env,
  agentRouting,
}: {
  connections: PlatformConnection[] | null | undefined;
  pypeId: string | undefined;
  env: string | undefined;
  agentRouting?: string | null;
}): ConnectionBinding => {
  const bound = findBoundConnectionIds(connections, pypeId, env);
  const agentPro = findBoundAgentProConnectionIds(connections, pypeId, env);

  return {
    selectedId: bound[0] ?? '',
    fromConnectionIds: [...bound.slice(0, 1), ...agentPro],
    unconnectedStatus:
      bound.length > 0
        ? null
        : agentPro.length > 0 || isAgentProPlatform(agentRouting)
          ? 'agentpro'
          : 'none',
  };
};
